import * as yup from 'yup'
import { noodlesAddressSchema, noodlesRemittanceInstructionsSchema } from './noodles-order.js'
import { MakeRequiredAndNullable } from './utils.js'

export enum NoodlesInvoiceLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, refernce ID, etc
  description = 'description', // verbose description of the item (human parseable)
  unitPrice = 'unitPrice', // price per unit
  itemQty = 'itemQty', // quantity of the item for order
  itemNote = 'itemNote', // note for the item
  itemDiscountRate = 'itemDiscountRate', // discount rate for the item
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
  itemWeight = 'itemWeight', // weight of the item
}

export enum NoodlesInvoicePaymentKeys {
  paymentNumber = 'paymentNumber', // payment number, not to be entered by user
  date = 'date', // date of the transaction
  netAmount = 'netAmount', // net amount of the transaction
  taxRate = 'taxRate', // tax rate of the transaction
  totalAmount = 'totalAmount', // totalAmount of the transaction
}

export const noodlesInvoiceFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: noodlesAddressSchema.nullable().optional(),
    firstName: yup.string().nullable().optional(),
    lastName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  invoiceNumber: yup.string().nullable().optional(),
  orderNumber: yup.string().nullable().optional(),
  customerNumber: yup.string().nullable().optional(),
  paymentMethod: yup.string().nullable().optional(),
  deliveryMethod: yup.string().nullable().optional(),
  weight: yup.string().nullable().optional(),
  vatId: yup.string().nullable().optional(),
  deliveryNoteId: yup.string().nullable().optional(),
  deliveryDate: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  externalNote: yup.string().nullable().optional(),
  customer: yup.object().shape({
    invoiceRecipient: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        invoiceRecipientAddress: noodlesAddressSchema.nullable().optional(),
        firstName: yup.string().nullable().optional(),
        lastName: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        itemNote: yup.string().nullable().optional(),

        itemDiscountRate: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
        itemWeight: yup.string().nullable().optional(),
      }),
    )
    .required(),
  taxRate: yup.string().nullable().optional(),
  shippingFee: yup.string().nullable().optional(),
  otherFees: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  remittanceInstructions: noodlesRemittanceInstructionsSchema,
  isNet: yup.string().nullable().optional(),
  showPaymentBalance: yup.string().nullable().optional(),
  showPaymentLink: yup.string().nullable().optional(),
  showBankDetails: yup.string().nullable().optional(),
  payments: yup
    .array()
    .of(
      yup.object().shape({
        paymentNumber: yup.number().nullable().optional(),
        date: yup.string().nullable().optional(),
        netAmount: yup.string().nullable().optional(),
        taxRate: yup.string().nullable().optional(),
        totalAmount: yup.string().nullable().optional(),
      }),
    )
    .required(),
  paymentBalance: yup.string().nullable().optional(),
})

export type NoodlesInvoiceFileData = yup.InferType<typeof noodlesInvoiceFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<NoodlesInvoiceFileData>

export const noodlesInvoiceFileMappings: RequiredFileData = {
  issuer: {
    companyName: '02ee57ad-dcd2-4322-9fd5-e678de7ea739',
    address: {
      addressLine1: 'b44a334c-19d2-460e-b788-a3e1bc8cc09f',
      addressLine2: '300febd7-6338-458c-93dd-34c710e53d9e',
      postalCode: 'e294788b-3210-4c7c-9229-41d5fd9950e9',
      city: '22e3af34-294d-48b1-9f70-987b553cde97',
      country: '298d4ce9-728d-4a57-a6e3-8b83f5f6737c',
    },
    firstName: '0b8361ef-c8f1-4085-893d-93a932cf67ed',
    lastName: 'dd5a02ec-a339-4ddd-af36-34e6cbebf648',
    phoneNumber: '9b15a78b-0a4f-4f04-bfcb-9339af8395f7',
    emailAddress: 'c055bcac-ee91-4493-89f9-38532a862bf3',
  },
  invoiceNumber: 'ba116b49-1dba-48c0-ad00-faaf36acb74b',
  orderNumber: '7f36f9b3-f422-40af-8beb-30a2d497ddfc',
  customerNumber: '324e685b-309f-47de-a203-cadfa815f521',
  paymentMethod: '48733699-c887-4309-a004-6beba027448f',
  deliveryMethod: 'd7f7f8f4-906e-4931-80e1-236186db08a6',
  weight: '92ced3c7-2698-4e0b-9649-23a77794faa1',
  vatId: '6a3356c8-b214-4dd9-96d6-4dee551a946b',
  deliveryNoteId: 'b3c37e85-ca96-46cf-96c1-fcd229710766',
  deliveryDate: '9f8f44c6-8c6f-43a6-b827-ce25af0a82f9',
  dateIssued: 'ef4cb941-5c99-4985-aa5c-ef0fe36e95c7',
  externalNote: '91ca6e69-f4d6-4640-8188-19a7cfb8a84b',
  customer: {
    invoiceRecipient: {
      firstName: '84e46710-c632-460d-ae0f-9fccaf6c2610',
      lastName: '664bfc6a-b366-4edd-b676-9d654d478229',
      companyName: 'aab50bac-55a3-4fe6-a2ad-450d836494c7',
      invoiceRecipientAddress: {
        addressLine1: 'ec3c2cb3-f35f-45d8-99eb-51870579a178',
        addressLine2: 'ac884bf6-4c7a-4042-b373-73f4a778e530',
        postalCode: 'b6da5cf0-03f7-41ac-84b7-a864ffdbe666',
        city: '9c4ee351-f399-4596-bac8-5b3798415a38',
        country: 'c313e74e-e3e3-4d3c-8adc-62e6b291ab60',
      },
      phoneNumber: '55d1d68f-13df-417c-8187-d621f5a1f91e',
      emailAddress: '4960f3a1-d0d1-4d40-9b8f-f7262049e760',
    },
  },
  items: [
    {
      itemNumber: null,
      itemName: '65d54954-5465-46d9-995d-b9460e560649',
      description: '0d580ea3-8a55-4f69-b0da-86c001a1f074',
      itemQty: 'ee10c022-c87f-44ff-82df-14a1275fc405',
      itemNote: 'b582f049-b0d3-4930-b3c7-1dd7ae6b5634',

      itemDiscountRate: '48b7b90c-d78c-4a17-b4ba-84950117314c',
      unitPrice: 'c55e3e66-843a-409b-b0a7-6c9614f6d76d',
      lineTotal: null,
      itemWeight: 'c710ad78-1445-42e6-942a-0e19ffd1a9ef',
    },
  ],
  taxRate: '7d6a342e-b01b-4887-bd08-7aa76d76743f',
  shippingFee: '10e50907-7842-4f09-bfdb-e4d5f240fbb6',
  otherFees: 'd51a8420-5827-4b5b-9d93-9f51fbe8b7ad',
  agreement: 'b68f1eb7-22ab-493a-8268-bd3205d4ba96',
  instructions: 'f2b978f1-d4d4-48d6-bef5-0d1fe8e5b21d',
  remittanceInstructions: {
    bankName: '74678dc5-a749-4f16-a29c-7d665f904f80',
    bankAddress: {
      addressLine1: '6ffe13a9-202e-4d55-a899-30b50a355845',
      addressLine2: '9f05f141-4280-48ef-b181-51256433eb39',
      postalCode: 'fcb520d3-af06-47c8-aee6-a692143110ea',
      city: '13c259e0-1a7c-4981-aea9-0e26786b7669',
      country: '6ccf4e8f-77df-49ff-a2f1-60e59a6be6c9',
    },
    kto: 'baca6849-d952-4d1c-976c-5f01f783eed5',
    blz: 'da406ba8-20d3-4e99-a09f-10abde737ec7',
    iban: '40cc9669-f4ea-4011-b380-7f75b17c0096',
    swift: '931099c2-1d30-475f-baee-3b406f45f631',
    firstName: '92e54478-b42e-457b-a86e-ee7e1f28b271',
    lastName: '6a5e22f6-5c06-400a-abe6-25c8c39f5b6a',
    ustIdNr: '396fc1a0-1431-49ef-b781-dd3cc89bd097',
    beneficiaryName: '090ca927-4ccc-47c2-8fa1-1e09435fd863',
    beneficiaryAddress: {
      addressLine1: '81d664d2-ad3a-431a-b1dd-1b48428df3c9',
      addressLine2: 'd6d69a9c-7311-4eff-bb84-5f22a78783c3',
      postalCode: '8796dc6c-9b0e-4df9-9c21-79ef2c86d5cb',
      city: '1d2dc193-1ecc-4894-9c0e-8900529ad12b',
      country: '39be7e5f-81c8-40bf-81a8-2bfe981f5f19',
    },
    telephone: 'f96e7690-6229-469e-ba91-2f3a59573824',
    telefax: '36f20e71-ac17-427a-b74e-b4a00d94905a',
    email: '6210acfe-9532-4473-8cdd-cae8886ea83e',
    website: '715fc6f0-88bb-439b-8930-f8ac9537a9b1',
    paymentLink: '2f4d9228-e592-447c-966b-40b2032ceaee',
  },
  isNet: '49bc287c-97e5-452b-85b4-c861b889b0f8',
  showPaymentBalance: '3e9207fa-7651-44f4-bc3b-7a62677d883f',
  showPaymentLink: '6b692e10-e417-4c81-98d9-5e4d52a12cc7',
  showBankDetails: '5ec5d6e9-5a70-4168-bec9-d8e3797ebf74',
  payments: [
    {
      paymentNumber: null,
      date: 'c24046fa-bbe7-4f4a-9095-8481b04a4d45',
      netAmount: 'a8d049ba-5ce6-4b8f-910c-50b592e7afd5',
      taxRate: null,
      totalAmount: 'f55c9ab3-299b-406d-9c62-31d534ab91a5',
    },
  ],
  paymentBalance: 'f1dd9cdb-bbba-4fe5-b7cc-85051f1a4209',
}
